// SignalMax canonical authenticated fetch for client components (platform-neutral).
//
// The browser Supabase session lives in localStorage per-origin, and most origins have no auth
// cookie (SessionCookieSync is mounted only in Careers), so a plain fetch with credentials but no
// Authorization header returns 401. requireUser accepts a Bearer token, so this attaches the
// localStorage access token as Authorization. No auto-redirect, so callers keep their own 401
// handling.
//
// This is THE single Bearer-attach implementation. getAccessToken reads the session token in one
// place; the product-specific wrappers delegate here and add only their own policy on top:
//   web/app/venture/ventureFetch.ts       bounces to /venture/login on 401
//   web/app/inflecta-app/inflectaFetch.ts sends credentials:"include" (its own origin, no cookie)
//   web/lib/sales/salesFetch.ts           adds nothing (plain same-origin)
// web/components/max/useCareerApi.ts (a JSON/envelope client, not a fetch wrapper) reuses
// getAccessToken so the access token is read in exactly one place across the platform.
import { supabase } from "@/lib/supabaseClient"

/** The browser session's access token, or undefined when signed out. getSession can throw on a
 *  corrupt or locked localStorage session, so this degrades to undefined (an unauthenticated
 *  request the route answers with 401, which callers already handle) instead of rejecting. */
export async function getAccessToken(): Promise<string | undefined> {
  try {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token
  } catch {
    return undefined
  }
}

export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken()
  const headers = new Headers(init.headers || {})
  if (token) headers.set("authorization", `Bearer ${token}`)
  return fetch(input, { credentials: "same-origin", ...init, headers })
}
